import { FC, useCallback, useRef, useState } from 'react';
import { Field, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';

import {
  ComponentProps,
  EcomOnly,
  isColorBright,
  resolveColorProperty,
  useEnabledFeatures,
  useJabraTheme,
  useSiteSettings,
  withTheme,
} from '@gn-audio/jss-components';
import {
  FeaturesSettings,
  SitecoreImage,
  SiteSettings,
  ThresholdMessage,
} from '@gn-audio/jss-core';

import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import CircleLink from 'common/components/CircleLink/CircleLink';
import Drawer from 'common/components/Drawer/Drawer';
import DrawerSliderItem from 'common/components/Drawer/DrawerSliderItem';
import ArrowSliderNavigation from 'common/components/JabraSlider/ArrowSliderNavigation';
import { JabraSlider, JabraSliderRef } from 'common/components/JabraSlider/JabraSlider';
import { IJabraTheme } from 'common/jabraTheme/JabraTheme.models';
import { usePageProduct } from 'common/PageProductContext/pageProductContext';

export type Benefit = {
  id: string;
  displayName: string;
  name: string;
  Title: Field<string>;
  BenefitIcon: Field<SitecoreImage>;
  Description: Field<string>;
  Image: Field<SitecoreImage>;
  Link?: LinkField;
  LinkTitle: Field<string>;
  Remark: Field<string>;
};

export type EcomBenefitsProps = ComponentProps<{
  Background: Field<string>;
  Benefits: Benefit[];
}>;

type FeaturedBenefit = {
  [key: string]: {
    shouldShow?: (features: FeaturesSettings) => boolean;
    getTitle: (settings: SiteSettings, product?: StandardPriceModel | null) => string | undefined;
  };
};

const getThresholdMessage = (thresholds: ThresholdMessage[], price?: number) => {
  if (!price) return undefined;
  if (thresholds.length === 0) return undefined;
  const matches = thresholds.filter((threshold) => price && threshold.Threshold <= price);
  const foundReturn = matches.pop();
  return foundReturn?.Message;
};

const featureBenefits: FeaturedBenefit = {
  '100DaysReturn': {
    getTitle: ({ returns }: SiteSettings, price?: StandardPriceModel | null) => {
      return getThresholdMessage(returns?.thresholds || [], price?.listedPrice);
    },
  },
  FreeShipping: {
    getTitle: ({ shipping }: SiteSettings, price?: StandardPriceModel | null) => {
      return getThresholdMessage(shipping?.thresholds || [], price?.listedPrice);
    },
  },
  Klarna: {
    shouldShow: (features) => {
      return !!features.Klarna;
    },
    getTitle: () => undefined,
  },
};

const EcomBenefits: FC<EcomBenefitsProps> = ({ fields }) => {
  const settings = useSiteSettings();
  const features = useEnabledFeatures();
  const { price } = usePageProduct();
  const tempBenefits = fields?.Benefits.length ? fields.Benefits : [];

  const [currentItem, setCurrentItem] = useState<number | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const backgroundColorProperty = resolveColorProperty(fields?.Background?.value || '');
  const isBgDark = !isColorBright(backgroundColorProperty);
  const dataSourceTheme: IJabraTheme = {
    backgroundColor: backgroundColorProperty,
    darkTheme: isBgDark,
    foregroundColor: isBgDark ? 'white' : 'black',
  };
  const jabraTheme = useJabraTheme();
  const theme = jabraTheme ?? dataSourceTheme;
  const jabraSlider = useRef<JabraSliderRef>(null);
  const benefits = tempBenefits
    ?.filter((benefit) => {
      if (!featureBenefits[benefit.name]?.shouldShow) {
        return true;
      }
      return featureBenefits[benefit.name].shouldShow?.(features);
    })
    .map((benefit) => {
      const benefitFields = benefit;
      return {
        ...benefitFields,
        id: benefit.id,
        name: benefit.name,
        Title: {
          value:
            featureBenefits[benefit.name]?.getTitle(settings, price) || benefitFields.Title.value,
        },
      };
    });

  const activeBenefit = benefits ? benefits[activeIndex] : undefined;

  const handleNext = useCallback(() => {
    jabraSlider.current?.next();
  }, []);
  const handlePrev = useCallback(() => {
    jabraSlider.current?.prev();
  }, []);

  return (
    <EcomOnly>
      <div className="bg-theme container container-fluid">
        <div className="flex flex-wrap justify-center gap-3">
          {benefits?.map((benefit, index) => {
            return (
              <div key={benefit.id} className="flex w-[170px] justify-center md:w-[206px]">
                <CircleLink
                  image={benefit.BenefitIcon.value}
                  onClick={() => {
                    setCurrentItem(index);
                  }}
                  title={benefit.Title.value}
                  theme={theme}
                />
              </div>
            );
          })}
        </div>
        <Drawer
          theme={theme}
          open={currentItem !== undefined}
          close={() => setCurrentItem(undefined)}
          title={activeBenefit?.Title.value}
          description={activeBenefit?.Description.value}
        >
          <ArrowSliderNavigation
            onNext={handleNext}
            onPrev={handlePrev}
            total={benefits?.length}
            activeIndex={activeIndex}
          />
          <JabraSlider
            startIndex={currentItem}
            mainClassName="w-full pb-11"
            slideClassName="basis-full px-6 md:px-16"
            showSlideArrows={false}
            ref={jabraSlider}
            onChange={setActiveIndex}
          >
            {benefits.map((benefitItem, index) => {
              const isActive = activeIndex === index;
              const { id, Image, BenefitIcon, Title, Description, Remark, Link, LinkTitle } =
                benefitItem;
              return (
                <DrawerSliderItem
                  key={id}
                  active={isActive}
                  image={Image.value}
                  iconImage={BenefitIcon.value}
                  title={Title.value}
                  content={Description.value}
                  remark={Remark.value}
                  actionLink={{ href: Link?.value.href, title: LinkTitle.value }}
                />
              );
            })}
          </JabraSlider>
        </Drawer>
      </div>
    </EcomOnly>
  );
};

export default withTheme()(EcomBenefits);
